import { z } from 'zod';
import { TenantRegionSchema } from './tenantRegion';
import { normalizeTenantLocale, TenantLocaleSchema } from './schemas';
import type { TenantLocale } from './schemas';
import { CountryDetectionSource, SelfServiceChannelSchema, SelfServicePurpose, SelfServicePurposeSchema } from './selfService';

/**
 * Contrato da assinatura self-service por In-App Purchase no iOS: constantes do
 * produto, regras de país, id do tenant e os schemas das bordas (callables,
 * documentos de reserva/OTP, notificação da App Store).
 *
 * Origem: #942 — 2026-10-12 11:56 +0200.
 *
 * Por quê: até 2026-10-12 o iOS não vendia nada (#106 §6). A Apple exige IAP
 * para publicar, e o owner decidiu: quem faz login sem estabelecimento vai
 * direto para uma assinatura mensal all-in-one (US$ 249 na época; o preço é o
 * do App Store Connect — #969/#970), 7 dias grátis, e o
 * tenant nasce na assinatura — sem ID fiscal, sem dados da empresa, com o país
 * DETECTADO pelo storefront da Apple. É canal ADICIONAL: CRM, Asaas e Stripe
 * seguem iguais.
 *
 * Usado por: Functions `apps/functions/src/iap/*` (reserva, ativação, webhook
 * ASSN, reconciliação) e `apps/functions/src/emailOtp/*`; iOS (via snapshot do
 * contrato e callables `reserveSelfServiceTenant`, `activateAppStorePurchase`,
 * `requestEmailOtp`, `confirmEmailOtp`).
 *
 * Arquitetura: o servidor é a fonte da verdade — país vem da JWS verificada
 * (nunca do cliente), e a assinatura só vale depois que a Function confere a
 * transação com a cadeia de certificados da Apple.
 *
 * Issues: #942 (épico), #106 (escopo do iOS, §6 revisto), #744 (D11 revisto só
 * para o OTP de e-mail do self-service).
 */

/** Produto auto-renovável do plano all-in-one (App Store Connect), o do 1º estabelecimento. */
export const IAP_ALL_IN_ONE_PRODUCT_ID = 'com.etiquetachef.app.allinone.monthly';

/**
 * Produtos "slot" do estabelecimento adicional na App Store. Cada um fica no
 * SEU PRÓPRIO grupo de assinatura e não tem intro offer, ou seja, não tem
 * trial.
 *
 * Origem: #970 — 2026-10-14 16:58 +0200.
 *
 * Por quê: no iOS a venda é obrigatoriamente por IAP (owner, 2026-10-14), e
 * um Apple ID só tem UMA assinatura ativa por grupo. Comprar o mesmo produto
 * para um segundo estabelecimento devolveria "já assinado". Por isso há um
 * grupo e um produto por estabelecimento adicional, até 6 por conta. O
 * servidor escolhe o 1º slot livre na reserva. O cliente informa os produtos
 * que o Apple ID já possui (`ownedProductIds`) porque o Apple ID não é o uid.
 *
 * Usado por: Functions `reserveSelfServiceTenant` (escolha) e
 * `provisionSelfServiceTenant` (aceite); iOS `SelfServicePaywallViewModel`.
 */
export const IAP_ADDITIONAL_SLOT_PRODUCT_IDS = [
  'com.etiquetachef.app.allinone.monthly.slot2',
  'com.etiquetachef.app.allinone.monthly.slot3',
  'com.etiquetachef.app.allinone.monthly.slot4',
  'com.etiquetachef.app.allinone.monthly.slot5',
  'com.etiquetachef.app.allinone.monthly.slot6',
] as const;

/** É um produto all-in-one da App Store (o base ou um slot do adicional)? (#970) */
export function isIapAllInOneProductId(productId: string | null | undefined): boolean {
  if (!productId) return false;
  const slots: readonly string[] = IAP_ADDITIONAL_SLOT_PRODUCT_IDS;
  return productId === IAP_ALL_IN_ONE_PRODUCT_ID || slots.includes(productId);
}

// Retirados em 2026-10-15 (#970, owner): `IAP_REFERENCE_AMOUNT_CENTS` (US$ 249)
// e `IAP_REFERENCE_CURRENCY`. O preço da assinatura é o que a loja informa na
// compra (`storeReferencePrice` em `apps/functions/src/selfService/selfServicePricing.ts`);
// preço fixo no código ficou falso no dia em que o preço mudou na loja (#969).

/** Oferta introdutória "free trial" cadastrada no App Store Connect. */
export const IAP_TRIAL_DAYS = 7;

/**
 * Países onde o produto NÃO é publicado (owner, 2026-10-12): China, Rússia,
 * Irã, Coreia do Norte e Síria. Também desmarcados na disponibilidade do app
 * no App Store Connect; o servidor recusa mesmo assim (defesa em profundidade).
 */
export const IAP_EXCLUDED_COUNTRIES = ['CN', 'RU', 'IR', 'KP', 'SY'] as const;

export function isIapExcludedCountry(alpha2: string): boolean {
  const excluded: readonly string[] = IAP_EXCLUDED_COUNTRIES;
  return excluded.includes(alpha2.toUpperCase());
}

/**
 * ISO 3166-1 alpha-3 → alpha-2. A Apple manda o storefront em alpha-3
 * (`JWSTransactionDecodedPayload.storefront`, ex. `"ITA"`); o tenant guarda
 * alpha-2 (`TenantCountryCodeSchema`). Tabela completa da ISO (249 códigos),
 * não só os territórios atuais da App Store: território novo não exige
 * release.
 */
const ALPHA3_TO_ALPHA2: Readonly<Record<string, string>> = {
  AFG: 'AF', ALA: 'AX', ALB: 'AL', DZA: 'DZ', ASM: 'AS', AND: 'AD', AGO: 'AO', AIA: 'AI',
  ATA: 'AQ', ATG: 'AG', ARG: 'AR', ARM: 'AM', ABW: 'AW', AUS: 'AU', AUT: 'AT', AZE: 'AZ',
  BHS: 'BS', BHR: 'BH', BGD: 'BD', BRB: 'BB', BLR: 'BY', BEL: 'BE', BLZ: 'BZ', BEN: 'BJ',
  BMU: 'BM', BTN: 'BT', BOL: 'BO', BES: 'BQ', BIH: 'BA', BWA: 'BW', BVT: 'BV', BRA: 'BR',
  IOT: 'IO', BRN: 'BN', BGR: 'BG', BFA: 'BF', BDI: 'BI', CPV: 'CV', KHM: 'KH', CMR: 'CM',
  CAN: 'CA', CYM: 'KY', CAF: 'CF', TCD: 'TD', CHL: 'CL', CHN: 'CN', CXR: 'CX', CCK: 'CC',
  COL: 'CO', COM: 'KM', COG: 'CG', COD: 'CD', COK: 'CK', CRI: 'CR', CIV: 'CI', HRV: 'HR',
  CUB: 'CU', CUW: 'CW', CYP: 'CY', CZE: 'CZ', DNK: 'DK', DJI: 'DJ', DMA: 'DM', DOM: 'DO',
  ECU: 'EC', EGY: 'EG', SLV: 'SV', GNQ: 'GQ', ERI: 'ER', EST: 'EE', SWZ: 'SZ', ETH: 'ET',
  FLK: 'FK', FRO: 'FO', FJI: 'FJ', FIN: 'FI', FRA: 'FR', GUF: 'GF', PYF: 'PF', ATF: 'TF',
  GAB: 'GA', GMB: 'GM', GEO: 'GE', DEU: 'DE', GHA: 'GH', GIB: 'GI', GRC: 'GR', GRL: 'GL',
  GRD: 'GD', GLP: 'GP', GUM: 'GU', GTM: 'GT', GGY: 'GG', GIN: 'GN', GNB: 'GW', GUY: 'GY',
  HTI: 'HT', HMD: 'HM', VAT: 'VA', HND: 'HN', HKG: 'HK', HUN: 'HU', ISL: 'IS', IND: 'IN',
  IDN: 'ID', IRN: 'IR', IRQ: 'IQ', IRL: 'IE', IMN: 'IM', ISR: 'IL', ITA: 'IT', JAM: 'JM',
  JPN: 'JP', JEY: 'JE', JOR: 'JO', KAZ: 'KZ', KEN: 'KE', KIR: 'KI', PRK: 'KP', KOR: 'KR',
  KWT: 'KW', KGZ: 'KG', LAO: 'LA', LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR', LBY: 'LY',
  LIE: 'LI', LTU: 'LT', LUX: 'LU', MAC: 'MO', MDG: 'MG', MWI: 'MW', MYS: 'MY', MDV: 'MV',
  MLI: 'ML', MLT: 'MT', MHL: 'MH', MTQ: 'MQ', MRT: 'MR', MUS: 'MU', MYT: 'YT', MEX: 'MX',
  FSM: 'FM', MDA: 'MD', MCO: 'MC', MNG: 'MN', MNE: 'ME', MSR: 'MS', MAR: 'MA', MOZ: 'MZ',
  MMR: 'MM', NAM: 'NA', NRU: 'NR', NPL: 'NP', NLD: 'NL', NCL: 'NC', NZL: 'NZ', NIC: 'NI',
  NER: 'NE', NGA: 'NG', NIU: 'NU', NFK: 'NF', MKD: 'MK', MNP: 'MP', NOR: 'NO', OMN: 'OM',
  PAK: 'PK', PLW: 'PW', PSE: 'PS', PAN: 'PA', PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH',
  PCN: 'PN', POL: 'PL', PRT: 'PT', PRI: 'PR', QAT: 'QA', REU: 'RE', ROU: 'RO', RUS: 'RU',
  RWA: 'RW', BLM: 'BL', SHN: 'SH', KNA: 'KN', LCA: 'LC', MAF: 'MF', SPM: 'PM', VCT: 'VC',
  WSM: 'WS', SMR: 'SM', STP: 'ST', SAU: 'SA', SEN: 'SN', SRB: 'RS', SYC: 'SC', SLE: 'SL',
  SGP: 'SG', SXM: 'SX', SVK: 'SK', SVN: 'SI', SLB: 'SB', SOM: 'SO', ZAF: 'ZA', SGS: 'GS',
  SSD: 'SS', ESP: 'ES', LKA: 'LK', SDN: 'SD', SUR: 'SR', SJM: 'SJ', SWE: 'SE', CHE: 'CH',
  SYR: 'SY', TWN: 'TW', TJK: 'TJ', TZA: 'TZ', THA: 'TH', TLS: 'TL', TGO: 'TG', TKL: 'TK',
  TON: 'TO', TTO: 'TT', TUN: 'TN', TUR: 'TR', TKM: 'TM', TCA: 'TC', TUV: 'TV', UGA: 'UG',
  UKR: 'UA', ARE: 'AE', GBR: 'GB', USA: 'US', UMI: 'UM', URY: 'UY', UZB: 'UZ', VUT: 'VU',
  VEN: 'VE', VNM: 'VN', VGB: 'VG', VIR: 'VI', WLF: 'WF', ESH: 'EH', YEM: 'YE', ZMB: 'ZM',
  ZWE: 'ZW', XKX: 'XK',
};

/**
 * Converte o storefront da Apple (alpha-3) no país do tenant (alpha-2).
 * Devolve `null` para código desconhecido — o chamador recusa a ativação em
 * vez de inventar país (o `tenantId` carrega o país e não muda depois).
 */
export function alpha3ToAlpha2(alpha3: string | null | undefined): string | null {
  if (alpha3 === null || alpha3 === undefined) return null;
  return ALPHA3_TO_ALPHA2[alpha3.trim().toUpperCase()] ?? null;
}

/**
 * Id de TODO tenant novo: `{CC}-{autoId do Firestore}` (ex.
 * `IT-a8Kp2Xn9Qm4Rt7Yz6BcD`).
 *
 * Origem: #942 — 2026-10-12 (self-service por IAP, como `selfServiceTenantId`);
 * generalizado em 2026-10-19 14:48 +0200 (#1005) para a venda assistida do CRM
 * e o tenant cortesia, que antes nasciam `{CC}-{documento fiscal}`.
 *
 * Por quê: decisão do owner (#1005) — um mesmo CNPJ/taxId pode ter VÁRIOS
 * tenants, então o documento fiscal não pode ser a chave. O vínculo
 * lead↔tenant do CRM é explícito (`resolveLeadTenantId` em
 * `apps/crm-functions/src/lib/tenantMatch.ts`), nunca derivado do CNPJ.
 *
 * Contrato: puro; lança `Error` se o país não for alpha-2 em caixa alta ou o
 * autoId não tiver o formato do Firestore (20 alfanuméricos). O autoId vem de
 * `db.collection('tenants').doc().id`.
 *
 * Usado por: `provisionTenantForSale`/`activatePartnerSale`
 * (`runSaleProvision`), `createCourtesyTenant`, `createSelfServiceCheckout` e
 * `selfServiceProvisioning` (via o alias `selfServiceTenantId`) — todos em
 * `apps/functions`.
 *
 * Arquitetura: quem precisar do país lê `tenant.countryCode`, nunca o prefixo;
 * quem precisar do documento lê `tenant.taxId`. Tenants antigos
 * `{CC}-{taxId}` mantêm o id (renomear doc é inviável: Auth, Storage, RTDB e
 * links apontam para ele). Única exceção a este formato: o tenant de teste
 * e2e `TEST_E2E_TENANT_ID` (`testModeTenants.ts`).
 *
 * Dicas: o webhook de billing reconhece este formato por regex
 * (`SELF_SERVICE_TENANT_ID` em `handlerContext.ts`) — desde #1005 ele casa
 * também com tenant de CRM, e quem decide é `self_service_origin`.
 *
 * Issues: #942/#969 (self-service), #1005 (todo tenant novo).
 */
export function newTenantId(countryAlpha2: string, autoId: string): string {
  if (!/^[A-Z]{2}$/.test(countryAlpha2)) throw new Error(`país inválido: ${countryAlpha2}`);
  if (!/^[A-Za-z0-9]{20}$/.test(autoId)) throw new Error('autoId do Firestore inválido');
  return `${countryAlpha2}-${autoId}`;
}

/**
 * Id do tenant self-service — alias de {@link newTenantId}, mantido porque é o
 * nome que o self-service (#942/#969) já usa. Origem: #942 — 2026-10-12.
 * Usado por: `createSelfServiceCheckout`, `selfServiceProvisioning`.
 */
export const selfServiceTenantId = newTenantId;

/** `external_subscription_id` da assinatura App Store (passa no regex de ids externos). */
export function appStoreSubscriptionId(originalTransactionId: string): string {
  return `aps_${originalTransactionId}`;
}

/**
 * Nome inicial do estabelecimento — editável depois em Configurações. O fluxo
 * não pergunta nada após o login (owner, 2026-10-12), então o nome sai da
 * conta: `displayName`, senão a parte local do e-mail, senão o fallback
 * traduzido que o chamador passa.
 */
export function defaultSelfServiceTenantName(
  displayName: string | null | undefined,
  email: string | null | undefined,
  fallback: string,
): string {
  const name = displayName?.trim();
  if (name) return name.slice(0, 120);
  const local = email?.split('@')[0]?.trim();
  // Relay do Sign in with Apple (`xyz123@privaterelay.appleid.com`) dá um
  // nome sem sentido: cai no fallback.
  if (local && !email?.toLowerCase().endsWith('@privaterelay.appleid.com')) {
    return local.slice(0, 120);
  }
  return fallback;
}

/**
 * Locale do tenant a partir do idioma do aparelho: entre os suportados
 * (`TenantLocaleSchema`), com `en` de fallback para idioma que o produto não
 * fala (ex. japonês).
 */
export function selfServiceTenantLocale(deviceLocale: string | null | undefined): TenantLocale {
  // Bug corrigido em 2026-10-12 21:30 +0200 (#942, teste no iPhone 12): o
  // iOS manda `Locale.current.identifier` com SUBLINHADO (`pt_IT`, `it_IT`);
  // `normalizeTenantLocale` só separa por hífen, e o tenant de quem usa o
  // aparelho em português nascia `en`. Troca `_` por `-` antes.
  const parsed = TenantLocaleSchema.safeParse(normalizeTenantLocale((deviceLocale ?? '').replace(/_/g, '-')));
  return parsed.success ? parsed.data : 'en';
}

// ---------- Reserva (antes da compra) ----------

export const SelfServiceReservationStatus = {
  /** Reserva criada; compra ainda não confirmada. */
  PENDING: 'PENDING',
  /** Tenant criado a partir desta reserva. */
  ACTIVATED: 'ACTIVATED',
  /** Passou de `expires_at` sem compra; limpa pela reconciliação. */
  EXPIRED: 'EXPIRED',
} as const;
export type SelfServiceReservationStatus =
  (typeof SelfServiceReservationStatus)[keyof typeof SelfServiceReservationStatus];
export const ALL_SELF_SERVICE_RESERVATION_STATUSES = Object.values(SelfServiceReservationStatus) as [
  SelfServiceReservationStatus,
  ...SelfServiceReservationStatus[],
];

/** Validade da reserva sem compra. */
export const SELF_SERVICE_RESERVATION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Identificadores de mídia paga que o cliente manda ao reservar o
 * self-service — o aparelho/navegador em que a compra vai acontecer.
 *
 * Origem: #972 — 2026-10-15 12:07 +0200 (conversão de início de trial para
 * Meta CAPI + GA4 Measurement Protocol).
 *
 * Por quê: a conversão sai do SERVIDOR (no nascimento do tenant), mas o match
 * com o anúncio depende do que só o cliente sabe: os cookies `_fbp`/`_fbc`/`_ga`
 * no web e o `app_instance_id` do Firebase nos apps. Todos os canais chamam
 * `reserveSelfServiceTenant` antes de pagar, então é o ponto único de captura.
 * Tudo opcional: cliente antigo, banner de cookies recusado ou Analytics
 * desligado simplesmente não mandam — a conversão sai com o que houver.
 *
 * Usado por: web `SubscribePage` (cookies), Android `PaywallViewModel` e iOS
 * `SelfServicePaywallViewModel` (app instance + aparelho) → callable
 * `reserveSelfServiceTenant`, que grava `ads_context` na reserva.
 *
 * Dados — entrada `ads` de `reserveSelfServiceTenant`:
 *   { "fbp": "fb.1.1790400000000.123456789", "fbc": "fb.1.1790400000000.IwAR…",
 *     "gaClientId": "123456789.1790400000",
 *     "appInstanceId": "8f1c…", "osVersion": "14", "appVersion": "1.42.0",
 *     "deviceModel": "Pixel 8" }
 * Valores crus, sem hash (a Meta exige `fbp`/`fbc` crus). Não é PII direta.
 */
export const SelfServiceAdsInputSchema = z.object({
  fbp: z.string().min(1).max(200).optional(),
  fbc: z.string().min(1).max(500).optional(),
  gaClientId: z.string().min(1).max(100).optional(),
  appInstanceId: z.string().min(1).max(200).optional(),
  osVersion: z.string().min(1).max(40).optional(),
  appVersion: z.string().min(1).max(40).optional(),
  deviceModel: z.string().min(1).max(80).optional(),
});
export type SelfServiceAdsInput = z.infer<typeof SelfServiceAdsInputSchema>;

/**
 * `ads_context` gravado na reserva: o que o cliente mandou (`SelfServiceAdsInput`,
 * em snake_case como o resto da reserva) mais IP e user agent lidos do request
 * do callable pelo servidor. Lido por `provisionSelfServiceTenantCore` ao
 * disparar a conversão (#972). Sobrescrito a cada `reserveSelfServiceTenant`
 * (o último aparelho que abriu o paywall é o que comprou).
 */
export const SelfServiceAdsContextSchema = z.object({
  fbp: z.string().nullable(),
  fbc: z.string().nullable(),
  ga_client_id: z.string().nullable(),
  app_instance_id: z.string().nullable(),
  os_version: z.string().nullable(),
  app_version: z.string().nullable(),
  device_model: z.string().nullable(),
  client_ip: z.string().nullable(),
  user_agent: z.string().nullable(),
  captured_at: z.number().int().positive(),
});
export type SelfServiceAdsContext = z.infer<typeof SelfServiceAdsContextSchema>;

/**
 * Documento `selfServiceReservations/{reservationId}` (coleção raiz, fechada a
 * cliente). O `reservationId` é um UUID v4 e vira o `appAccountToken` da
 * compra no StoreKit — é o elo assinado pela Apple entre a transação e o uid.
 *
 * Dados:
 *   { "uid": "uid-1", "email": "dono@example.com", "provider_ids": ["password"],
 *     "locale": "it", "time_zone": "Europe/Rome", "storefront_hint": "ITA",
 *     "status": "PENDING", "tenant_id": null,
 *     "created_at": 1790400000000, "expires_at": 1790486400000,
 *     "sales_notified_at": 1790400000100 }
 *
 * `storefront_hint` vem do aparelho e é só informativo (e-mail para vendas); o
 * país do tenant sai da JWS. `sales_notified_at` garante UM e-mail para
 * `sales@` por reserva, mesmo com a callable repetida.
 */
export const SelfServiceReservationSchema = z.object({
  uid: z.string().min(1),
  email: z.string().nullable(),
  provider_ids: z.array(z.string()),
  locale: TenantLocaleSchema,
  time_zone: z.string().min(1).max(64),
  storefront_hint: z.string().max(3).nullable(),
  status: z.enum(ALL_SELF_SERVICE_RESERVATION_STATUSES),
  tenant_id: z.string().nullable(),
  created_at: z.number().int().positive(),
  expires_at: z.number().int().positive(),
  sales_notified_at: z.number().int().positive().nullable(),
  // ---- #969 (2026-10-14 13:31 +0200) — canais Google Play e Stripe. Todos
  // opcionais: reserva gravada pelo iOS antes da #969 não os tem, e a do iOS
  // continua sem eles (ausente = `APP_STORE_IAP`).
  /** Canal que pediu a reserva (o último, se o usuário trocou de caminho). */
  channel: SelfServiceChannelSchema.optional(),
  /** País DETECTADO pelo servidor no checkout Stripe (alpha-2). */
  country: z.string().regex(/^[A-Z]{2}$/).nullable().optional(),
  country_source: z
    .enum([CountryDetectionSource.IP, CountryDetectionSource.COORDS, CountryDetectionSource.PLAY])
    .nullable()
    .optional(),
  /** Última sessão do Stripe Checkout criada para esta reserva. */
  stripe_session_id: z.string().nullable().optional(),
  /**
   * Id do tenant RESERVADO no checkout Stripe (`{CC}-{autoId}`, CC = país
   * detectado). Vai no `metadata.tenantId` da assinatura, para os handlers do
   * webhook que já existem acharem o tenant quando ele nascer. Gerado uma vez
   * por reserva e país; outro país → outro id.
   */
  planned_tenant_id: z.string().regex(/^[A-Z]{2}-[A-Za-z0-9]{20}$/).nullable().optional(),
  /**
   * #1078 (F2 da #960, 2026-10-29 09:31 +0100) — região em que o `planned_tenant_id` vai nascer,
   * resolvida UMA vez junto com o id (`resolveRegionForNewTenant`) e reaproveitada com ele: a
   * ativação pelo webhook lê DAQUI e nunca recalcula, senão uma mudança no mapa de países ou na
   * chave `config/regions` entre o checkout e o pagamento criaria o tenant noutro banco. Ausente
   * (reserva anterior à F2) = `southamerica-east1`. Não vai no `metadata` da Stripe: mudar os
   * parâmetros da sessão quebraria a chave de idempotência (`createSelfServiceCheckout`).
   */
  planned_region: TenantRegionSchema.nullable().optional(),
  /**
   * Token do User Choice Billing (Android escolheu a Stripe): o servidor o
   * reporta à Play como transação externa na ativação.
   */
  external_transaction_token: z.string().nullable().optional(),
  /**
   * #970 — para que é a compra. Ausente = `FIRST` (reservas da #942/#969). O
   * checkout e a ativação leem DAQUI, não do cliente: a reserva `ADDITIONAL`
   * passou pela porta do adicional (OWNER de venda direta) e não tem trial.
   */
  purpose: SelfServicePurposeSchema.optional(),
  /**
   * #970 — produto da loja escolhido na reserva (base ou slot do adicional).
   * Ausente = o produto base do canal.
   */
  product_id: z.string().max(200).optional(),
  /**
   * #972 — identificadores de mídia paga do último aparelho que reservou
   * (`SelfServiceAdsContextSchema`). Ausente em reserva anterior à #972 ou de
   * cliente que não mandou `ads`.
   */
  ads_context: SelfServiceAdsContextSchema.optional(),
  /**
   * #978 — só em reserva `RESUBSCRIBE`: o estabelecimento suspenso que a compra
   * vai religar. Gravado pelo `reserveSelfServiceTenant` depois da porta
   * `assertCanResubscribe`; a ativação lê DAQUI, nunca do cliente.
   */
  target_tenant_id: z.string().min(1).max(64).optional(),
});
export type SelfServiceReservation = z.infer<typeof SelfServiceReservationSchema>;

/** Entrada de `reserveSelfServiceTenant`. */
export const ReserveSelfServiceTenantInputSchema = z.object({
  /** Idioma do aparelho (BCP-47 livre) — vira o locale do tenant. */
  deviceLocale: z.string().min(1).max(35),
  /** Fuso IANA do aparelho (`TimeZone.current.identifier`). */
  timeZone: z.string().min(1).max(64),
  /** `Storefront.current?.countryCode` (alpha-3). Só pista para vendas. */
  storefrontHint: z.string().regex(/^[A-Z]{3}$/).nullish(),
  /**
   * #969 — canal que vai pagar. Ausente = `APP_STORE_IAP` (o iOS da #942 não
   * manda o campo, e não muda). Decide o `productId` devolvido e o texto do
   * e-mail para vendas.
   */
  channel: SelfServiceChannelSchema.optional(),
  /**
   * #970 — ausente = `FIRST`. `ADDITIONAL` = OWNER de venda direta abrindo
   * mais um estabelecimento (menu Mais): outra porta, outro produto, sem trial.
   */
  purpose: SelfServicePurposeSchema.optional(),
  /**
   * #970 — produtos de assinatura que a conta da LOJA já possui no aparelho
   * (`Transaction.currentEntitlements` no iOS, `queryPurchasesAsync` na Play).
   * O Apple ID e a conta Google não são o uid, então só o aparelho sabe disso.
   * O servidor não escolhe esses produtos como slot. É só uma pista para
   * evitar "já assinado"; a decisão é do servidor.
   */
  ownedProductIds: z.array(z.string().min(1).max(200)).max(20).optional(),
  /**
   * #972 — identificadores de mídia paga do aparelho/navegador (cookies no
   * web, `app_instance_id` nos apps). Ausente = cliente anterior à #972.
   */
  ads: SelfServiceAdsInputSchema.optional(),
  /**
   * #978 — estabelecimento a religar; obrigatório com `purpose: RESUBSCRIBE` e
   * proibido nas outras finalidades. O servidor confere que quem chama é o
   * OWNER e que o tenant está suspenso pela App Store.
   */
  tenantId: z.string().min(1).max(64).optional(),
}).superRefine((input, ctx) => {
  // #978 — `tenantId` e `RESUBSCRIBE` andam juntos: um sem o outro é cliente
  // com bug, e a recusa aqui evita que uma reserva FIRST carregue um alvo.
  const resubscribe = input.purpose === SelfServicePurpose.RESUBSCRIBE;
  if (resubscribe !== (input.tenantId !== undefined)) {
    ctx.addIssue({
      code: 'custom',
      path: ['tenantId'],
      message: resubscribe ? 'tenantId é obrigatório na reassinatura' : 'tenantId só vale na reassinatura',
    });
  }
});
export type ReserveSelfServiceTenantInput = z.infer<typeof ReserveSelfServiceTenantInputSchema>;

export const ReserveSelfServiceTenantOutputSchema = z.object({
  /**
   * UUID — passar como `appAccountToken` na compra (iOS) ou como
   * `obfuscatedAccountId` no `BillingFlowParams` (Android, #969).
   */
  reservationId: z.uuid(),
  /** Produto da loja do canal (App Store ou Play); vazio nos canais Stripe. */
  productId: z.string(),
});
export type ReserveSelfServiceTenantOutput = z.infer<typeof ReserveSelfServiceTenantOutputSchema>;

/** Entrada de `activateAppStorePurchase`: `Transaction.jwsRepresentation` do StoreKit 2. */
export const ActivateAppStorePurchaseInputSchema = z.object({
  signedTransaction: z.string().min(20).max(20_000),
});
export type ActivateAppStorePurchaseInput = z.infer<typeof ActivateAppStorePurchaseInputSchema>;

export const ActivateAppStorePurchaseOutputSchema = z.object({
  tenantId: z.string().min(1),
  /** `true` quando a transação já tinha ativado o tenant (reentrada). */
  alreadyActive: z.boolean(),
  /**
   * #1078 (F2 da #960) — região onde o tenant vive. Opcional: servidor antigo não a devolve e o
   * cliente trata ausente como `southamerica-east1`. Os clientes passam a usá-la na F5 para abrir
   * o banco/Functions do tenant sem esperar o diretório (#1081-#1083).
   */
  region: TenantRegionSchema.optional(),
});
export type ActivateAppStorePurchaseOutput = z.infer<typeof ActivateAppStorePurchaseOutputSchema>;

// ---------- OTP de e-mail (só contas e-mail/senha) ----------

/** Código de 6 dígitos; TTL, tentativas e cooldown espelham o OTP de SMS. */
export const EMAIL_OTP_TTL_MS = 10 * 60 * 1000;
export const EMAIL_OTP_MAX_ATTEMPTS = 3;
export const EMAIL_OTP_LOCK_MS = 24 * 60 * 60 * 1000;
export const EMAIL_OTP_COOLDOWN_MS = 60 * 1000;
export const EMAIL_OTP_MAX_SENDS_PER_WINDOW = 5;
export const EMAIL_OTP_SEND_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * `requestEmailOtp`: o e-mail é SEMPRE o da conta autenticada (nunca do
 * cliente). `deviceLocale` (opcional) escolhe o idioma do e-mail; sem ele, o
 * servidor usa o locale do perfil.
 */
export const RequestEmailOtpInputSchema = z
  .object({ deviceLocale: z.string().min(1).max(35).optional() })
  .strict();
export type RequestEmailOtpInput = z.infer<typeof RequestEmailOtpInputSchema>;
export const RequestEmailOtpOutputSchema = z.object({
  /** Millis até poder pedir outro código. */
  cooldownMs: z.number().int().nonnegative(),
  /**
   * `true` quando a conta já estava verificada (outra aba, outro aparelho):
   * nenhum e-mail saiu e o cliente segue direto para a assinatura.
   */
  alreadyVerified: z.boolean().optional(),
});
export type RequestEmailOtpOutput = z.infer<typeof RequestEmailOtpOutputSchema>;

export const ConfirmEmailOtpInputSchema = z.object({
  code: z.string().regex(/^[0-9]{6}$/),
});
export type ConfirmEmailOtpInput = z.infer<typeof ConfirmEmailOtpInputSchema>;

/** Saída de `confirmEmailOtp`: só existe no sucesso (erro = `HttpsError`). */
export const ConfirmEmailOtpOutputSchema = z.object({
  verified: z.literal(true),
  /** `true` quando a conta já estava verificada antes desta chamada. */
  alreadyVerified: z.boolean(),
});
export type ConfirmEmailOtpOutput = z.infer<typeof ConfirmEmailOtpOutputSchema>;

/**
 * Documento `emailOtps/{sha256(email minúsculo)}` (fechado a cliente).
 *
 * Dados:
 *   { "uid": "uid-1", "code_hash": "<sha256 hex>", "expires_at": 1790400600000,
 *     "attempts": 0, "locked_until": null, "last_sent_at": 1790400000000,
 *     "send_window_start": 1790400000000, "sends_in_window": 1 }
 */
export const EmailOtpDocSchema = z.object({
  uid: z.string().min(1),
  code_hash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  expires_at: z.number().int().nonnegative(),
  attempts: z.number().int().nonnegative(),
  locked_until: z.number().int().nonnegative().nullable(),
  last_sent_at: z.number().int().nonnegative(),
  send_window_start: z.number().int().nonnegative(),
  sends_in_window: z.number().int().nonnegative(),
});
export type EmailOtpDoc = z.infer<typeof EmailOtpDocSchema>;

// ---------- App Store Server Notifications V2 ----------

/**
 * Tipos de notificação que o webhook trata. Lista de documentação, NÃO enum
 * fechado no wire (mesma decisão de `KNOWN_APP_VERSION_STATES`): tipo novo da
 * Apple responde 200 e é ignorado, em vez de 400 e reentrega eterna.
 */
export const APP_STORE_NOTIFICATION_TYPES = {
  SUBSCRIBED: 'SUBSCRIBED',
  DID_RENEW: 'DID_RENEW',
  DID_FAIL_TO_RENEW: 'DID_FAIL_TO_RENEW',
  GRACE_PERIOD_EXPIRED: 'GRACE_PERIOD_EXPIRED',
  EXPIRED: 'EXPIRED',
  REFUND: 'REFUND',
  REVOKE: 'REVOKE',
  DID_CHANGE_RENEWAL_STATUS: 'DID_CHANGE_RENEWAL_STATUS',
  TEST: 'TEST',
} as const;

/** Corpo HTTP do ASSN V2: só o JWS; o conteúdo vem depois da verificação. */
export const AppStoreNotificationBodySchema = z.object({
  signedPayload: z.string().min(20),
});

// ---------- Motivos de recusa (details.reason) ----------

/**
 * `details.reason` dos `HttpsError` das callables do self-service — o cliente
 * decide a tela pelo motivo, nunca pela mensagem (que é pt-BR, para log).
 *
 * Origem: #942 — 2026-10-12 12:10 +0200.
 *
 * Por quê: o mesmo código gRPC (`failed-precondition`) cobre casos que a tela
 * trata de jeitos opostos — e-mail ainda não verificado (vai para o OTP), já
 * é membro (vai para a Home), país excluído (aviso definitivo). Sem o motivo
 * tipado o iOS teria de comparar texto.
 *
 * Contrato: o servidor (`apps/functions/src/emailOtp/*`, `iap/*`) só lança
 * estes valores em `details.reason`; valor desconhecido no cliente = erro
 * genérico com "tentar de novo".
 *
 * Usado por: Functions `requestEmailOtp`, `confirmEmailOtp`,
 * `reserveSelfServiceTenant`, `activateAppStorePurchase`,
 * `detectSelfServiceCountry`/`createSelfServiceCheckout` (#969/#970); iOS,
 * Android e web (mapeamento de erro das telas de OTP e paywall).
 */
export const SelfServiceErrorReason = {
  /** Conta Google/Apple: o provedor já verificou o e-mail — não há OTP. */
  EMAIL_VERIFIED_BY_PROVIDER: 'email_verified_by_provider',
  /** Conta sem e-mail (não deveria acontecer no iOS). */
  NO_EMAIL: 'no_email',
  /** OTP: cooldown entre envios ainda correndo (`details.retryAfterSec`). */
  OTP_COOLDOWN: 'cooldown',
  /** OTP: teto de envios da janela atingido (`details.retryAfterSec`). */
  OTP_CAPPED: 'capped',
  /** OTP: bloqueado após tentativas erradas (`details.retryAfterSec`). */
  OTP_LOCKED: 'locked',
  /** OTP: nenhum código pendente, ou já vencido — pedir outro. */
  OTP_EXPIRED: 'otp_expired',
  /** OTP: código errado (`details.attemptsLeft`). */
  OTP_INVALID: 'otp_invalid',
  /** OTP: o e-mail não pôde ser entregue (endereço em blocklist). */
  EMAIL_UNDELIVERABLE: 'email_undeliverable',
  /** Reserva/ativação: e-mail da conta ainda não verificado — ir ao OTP. */
  EMAIL_NOT_VERIFIED: 'email_not_verified',
  /** Reserva/ativação: a conta já tem (ou teve) estabelecimento. */
  ALREADY_MEMBER: 'already_member',
  /**
   * #970 — estabelecimento adicional: a conta participa de um tenant vendido
   * por parceiro. Só a venda direta compra mais estabelecimentos sozinha.
   */
  PARTNER_CUSTOMER: 'partner_customer',
  /** #970 — estabelecimento adicional: a conta não é OWNER de nenhum tenant ativo. */
  NOT_OWNER: 'not_owner',
  /**
   * #970 — estabelecimento adicional: todos os produtos slot da loja já estão
   * em uso por esta conta (ou ainda não foram cadastrados na loja).
   */
  ADDITIONAL_LIMIT_REACHED: 'additional_limit_reached',
  /** Conta desativada/excluída — só o suporte reativa. */
  ACCOUNT_DISABLED: 'account_disabled',
  /** Fuso do aparelho não é IANA válido. */
  INVALID_TIME_ZONE: 'invalid_time_zone',
  /** Ativação: JWS inválida, de outro app ou de ambiente não aceito. */
  INVALID_TRANSACTION: 'invalid_transaction',
  /** Ativação: produto da transação não é o all-in-one. */
  WRONG_PRODUCT: 'wrong_product',
  /** Ativação: `appAccountToken` ausente, desconhecido ou de outra conta. */
  RESERVATION_MISMATCH: 'reservation_mismatch',
  /** Ativação: storefront da App Store em país excluído ou desconhecido. */
  COUNTRY_NOT_ALLOWED: 'country_not_allowed',
  /** Ativação: a transação já está expirada ou foi revogada/reembolsada. */
  TRANSACTION_INACTIVE: 'transaction_inactive',
  /** Ativação: falha transitória ao verificar com a Apple — tentar de novo. */
  VERIFICATION_RETRYABLE: 'verification_retryable',
  /**
   * #978 — reassinatura: o estabelecimento não está suspenso pela App Store
   * (ativo, cancelado, removido, pago por outro provedor, ou ainda em
   * cobrança da Apple), ou a finalidade veio por canal que não a aceita.
   */
  NOT_RESUBSCRIBABLE: 'not_resubscribable',
} as const;
export type SelfServiceErrorReason =
  (typeof SelfServiceErrorReason)[keyof typeof SelfServiceErrorReason];

/**
 * Conta e-mail/senha que, depois deste prazo, segue sem e-mail verificado e
 * sem estabelecimento é apagada pelo scheduler semanal (#942): é o lixo de
 * quem digitou o e-mail errado e criou outra conta.
 */
export const SELF_SERVICE_ABANDONED_ACCOUNT_MS = 7 * 24 * 60 * 60 * 1000;
